/**
 * Connection validation for ReactFlow's isValidConnection prop.
 *
 * This determines whether ReactFlow should allow a connection to be created
 * when a user drags between handles. This is different from handle visibility
 * logic (which determines which handles to show during dragging).
 *
 * Usage: <ReactFlow isValidConnection={isValidConnection} ... />
 */
import {Connection, Edge} from 'reactflow';

import {
  ProcessFlowchartNodeDetailsType,
  ProcessFlowchartPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FlowchartFeatures,
  isFlowchartFeatureEnabled,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/FlowchartFeaturesContext';
import {
  ProcessFlowchartTaskSchemaNodeAdapter,
  ProcessFlowchartNodeAdapter,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {
  parseHandleId,
  HANDLE_TYPE_FIELD,
  HANDLE_TYPE_NODE,
  HANDLE_DIRECTION_SOURCE,
  HANDLE_DIRECTION_TARGET,
  FIELD_TYPE_INPUT,
  FIELD_TYPE_OUTPUT,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {ProcessFlowchartNodeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  extractControlFlowEdges,
  getFlowchartAdjacencyMapFromEdges,
} from 'util/graph-utils/getFlowchartAdjacencyMap';
import wouldEdgeCreateCycle from 'util/graph-utils/wouldEdgeCreateCycle';

export type NodeAdapterLookup = (
  nodeId: string
) => ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> | ProcessFlowchartNodeAdapter | null;

export type NodeDataLookup = (
  nodeId: string
) => {nodeDetailsType?: ProcessFlowchartNodeDetailsType; nodeType?: ProcessFlowchartNodeType} | null;

/**
 * Helper function to detect if adding an edge would create a cycle (loop).
 * Note: This only considers control flow edges, not data flow edges.
 *
 * @param sourceNodeId - The source node of the new edge
 * @param targetNodeId - The target node of the new edge
 * @param existingEdges - Array of existing edges to check for cycles
 * @returns true if adding the edge would create a loop, false otherwise
 */
function wouldCreateLoop(sourceNodeId: string, targetNodeId: string, existingEdges: Array<Edge>): boolean {
  const adjacencyMap = getFlowchartAdjacencyMapFromEdges(existingEdges);
  return wouldEdgeCreateCycle(adjacencyMap, sourceNodeId, targetNodeId);
}

/**
 * Validates linear flowchart constraints based on platform features.
 * Only applies to control flow edges.
 *
 * @param connection - The connection attempt
 * @param existingEdges - Array of existing edges
 * @param flowchartFeatures - Map of platform feature flags
 * @returns true if the connection passes linear flowchart rules, false to reject it
 */
function checkLinearFlowchartConstraints(
  connection: Connection,
  existingEdges: Array<Edge>,
  flowchartFeatures: FlowchartFeatures | undefined
): boolean {
  if (!connection.sourceHandle || !connection.targetHandle || !connection.source || !connection.target) {
    return false;
  }

  // Only apply these checks to control flow edges
  const sourceInfo = parseHandleId(connection.sourceHandle);
  if (sourceInfo.handleCategory !== HANDLE_TYPE_NODE) {
    return true;
  }

  if (
    !isFlowchartFeatureEnabled(
      flowchartFeatures,
      ProcessFlowchartPlatformFeatureType.ALLOW_BRANCHING_TRANSITIONS
    )
  ) {
    const hasOutgoingEdge = extractControlFlowEdges(existingEdges).some(
      (edge) => edge.source === connection.source
    );
    if (hasOutgoingEdge) {
      return false;
    }
  }

  if (
    !isFlowchartFeatureEnabled(
      flowchartFeatures,
      ProcessFlowchartPlatformFeatureType.ALLOW_MERGING_TRANSITIONS
    )
  ) {
    const hasIncomingEdge = extractControlFlowEdges(existingEdges).some(
      (edge) => edge.target === connection.target
    );
    if (hasIncomingEdge) {
      return false;
    }
  }

  if (!isFlowchartFeatureEnabled(flowchartFeatures, ProcessFlowchartPlatformFeatureType.ALLOW_LOOPS)) {
    if (wouldCreateLoop(connection.source, connection.target, existingEdges)) {
      return false;
    }
  }

  return true;
}

/**
 * Determines whether ReactFlow should allow a connection to be created between two handles.
 *
 * @package - exported for testing only, use isValidConnection instead
 * @param connection - The connection attempt (source/target handles and nodes)
 * @param existingEdges - Array of existing edges to check for duplicates
 * @param getNodeData - Function to lookup node data by node ID (needed for output node check)
 * @returns true if the connection should be allowed, false if it should be rejected
 */
export function shouldAllowConnection(
  connection: Connection,
  existingEdges: Array<Edge>,
  getNodeData: NodeDataLookup
): boolean {
  if (!connection.sourceHandle || !connection.targetHandle || !connection.source || !connection.target) {
    return false;
  }

  const isDuplicate = existingEdges.some(
    (edge) => edge.sourceHandle === connection.sourceHandle && edge.targetHandle === connection.targetHandle
  );
  if (isDuplicate) {
    return false;
  }

  const sourceInfo = parseHandleId(connection.sourceHandle);
  const targetInfo = parseHandleId(connection.targetHandle);

  // Handle self connections (a node looping back on itself)
  if (connection.source === connection.target) {
    if (sourceInfo.handleCategory === HANDLE_TYPE_FIELD && targetInfo.handleCategory === HANDLE_TYPE_FIELD) {
      // Allow input field's right handle -> input field's left handle (input loop)
      if (sourceInfo.fieldType === FIELD_TYPE_INPUT && targetInfo.fieldType === FIELD_TYPE_INPUT) {
        return true;
      }
      // Allow output field's right handle ->input field's left handle (output to input loop)
      if (sourceInfo.fieldType === FIELD_TYPE_OUTPUT && targetInfo.fieldType === FIELD_TYPE_INPUT) {
        return true;
      }
    }

    return false;
  }

  // Handle categories must match (node <> node, field <> field only)
  if (sourceInfo.handleCategory !== targetInfo.handleCategory) {
    return false;
  }

  // Direction must be source -> target (not source -> source or target -> target)
  if (sourceInfo.direction !== HANDLE_DIRECTION_SOURCE || targetInfo.direction !== HANDLE_DIRECTION_TARGET) {
    return false;
  }

  // For field handles, enforce input/output compatibility
  if (sourceInfo.handleCategory === HANDLE_TYPE_FIELD && targetInfo.handleCategory === HANDLE_TYPE_FIELD) {
    // Check if target node is an Output node (allows output connections for flowchart outputs)
    const targetNodeData = getNodeData(connection.target);
    const isTargetOutputNode =
      targetNodeData?.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;

    // Reject output -> output connections (unless target is an Output node)
    if (sourceInfo.fieldType === FIELD_TYPE_OUTPUT && targetInfo.fieldType === FIELD_TYPE_OUTPUT) {
      return isTargetOutputNode;
    }

    // Reject input -> output connections (unless target is an Output node)
    if (sourceInfo.fieldType === FIELD_TYPE_INPUT && targetInfo.fieldType === FIELD_TYPE_OUTPUT) {
      return isTargetOutputNode;
    }
  }

  return true;
}

/**
 * Helper function to check if a node has reached its limit of incoming or outgoing edges.
 */
function hasReachedEdgeLimit(
  nodeId: string,
  adapter: ProcessFlowchartNodeAdapter | ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown>,
  nodeData: {nodeDetailsType?: ProcessFlowchartNodeDetailsType; nodeType?: ProcessFlowchartNodeType},
  existingEdges: Array<Edge>,
  isOutgoing: boolean
): boolean {
  const isFlowchartOutput = nodeData.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
  const maxEdges = isOutgoing
    ? adapter.maxOutgoingEdges(isFlowchartOutput)
    : adapter.maxIncomingEdges(isFlowchartOutput);
  const currentEdges = existingEdges.filter((edge) =>
    isOutgoing ? edge.source === nodeId : edge.target === nodeId
  ).length;
  return currentEdges >= maxEdges;
}

/**
 * Validates connection against adapter blocking rules (maxIncomingEdges, maxOutgoingEdges, blockedAdjacentNodeTypes).
 *
 * @param connection - The connection attempt (source/target handles and nodes)
 * @param existingEdges - Array of existing edges to check edge counts
 * @param getNodeData - Function to lookup node data by node ID
 * @param getGenericNodeAdapter - Function to lookup node adapters by node ID
 * @returns true if the connection passes adapter blocking rules, false to reject it
 */
function checkAdapterBlockingRules(
  connection: Connection,
  existingEdges: Array<Edge>,
  getNodeData: NodeDataLookup,
  getNodeAdapter: NodeAdapterLookup
): boolean {
  if (!connection.source || !connection.target) {
    return false;
  }

  const sourceAdapter = getNodeAdapter(connection.source);
  const targetAdapter = getNodeAdapter(connection.target);
  const sourceNodeData = getNodeData(connection.source);
  const targetNodeData = getNodeData(connection.target);
  const sourceNodeIsNotPlaceholder = sourceAdapter != null && sourceNodeData != null;
  const targetNodeIsNotPlaceholder = targetAdapter != null && targetNodeData != null;

  // Check maxOutgoingEdges for source node
  if (
    sourceNodeIsNotPlaceholder &&
    hasReachedEdgeLimit(connection.source, sourceAdapter, sourceNodeData, existingEdges, true)
  ) {
    return false;
  }

  // Check maxIncomingEdges for target node
  if (
    targetNodeIsNotPlaceholder &&
    hasReachedEdgeLimit(connection.target, targetAdapter, targetNodeData, existingEdges, false)
  ) {
    return false;
  }

  // Check if node types are blocked from being adjacent
  if (sourceNodeIsNotPlaceholder && targetNodeIsNotPlaceholder) {
    const sourceBlockedTypes = sourceAdapter.blockedAdjacentNodeTypes();
    const targetBlockedTypes = targetAdapter.blockedAdjacentNodeTypes();
    if (
      (targetNodeData.nodeType && sourceBlockedTypes.includes(targetNodeData.nodeType)) ||
      (sourceNodeData.nodeType && targetBlockedTypes.includes(sourceNodeData.nodeType))
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Main validation function for ReactFlow's isValidConnection prop.
 * Combines default connection rules with optional adapter-specific validation.
 *
 * @param connection - The connection attempt (source/target handles and nodes)
 * @param existingEdges - Array of existing edges to check for duplicates
 * @param getNodeData - Function to lookup node data by node ID (needed for output node check)
 * @param getAdapterForNodeId - Optional function to lookup node adapters by node ID
 * @param flowchartFeatures - Optional map of platform feature flags for linear flowchart constraints
 * @returns true if ReactFlow should allow the connection, false to reject it
 */
export function isValidConnection(
  connection: Connection,
  existingEdges: Array<Edge>,
  getNodeData: NodeDataLookup,
  getAdapterForNodeId?: NodeAdapterLookup,
  flowchartFeatures?: FlowchartFeatures
): boolean {
  const isAllowedByDefault = shouldAllowConnection(connection, existingEdges, getNodeData);
  if (!isAllowedByDefault) {
    return false;
  }

  if (flowchartFeatures != null) {
    const passesLinearConstraints = checkLinearFlowchartConstraints(
      connection,
      existingEdges,
      flowchartFeatures
    );
    if (!passesLinearConstraints) {
      return false;
    }
  }

  // Check adapter blocking rules if adapter lookup is provided
  if (getAdapterForNodeId != null) {
    const passesBlockingRules = checkAdapterBlockingRules(
      connection,
      existingEdges,
      getNodeData,
      getAdapterForNodeId
    );
    if (!passesBlockingRules) {
      return false;
    }
  }

  // Check custom validation rules from task schema adapters
  if (getAdapterForNodeId != null && connection.source != null) {
    const sourceNodeAdapter = getAdapterForNodeId(connection.source);
    if (sourceNodeAdapter != null) {
      return sourceNodeAdapter.isConnectionValid?.(connection, existingEdges) ?? true;
    }
  }

  return true;
}
